import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { cancelLlmMode, endSession, resetSession, startOarsAttempt } from '../../services/api';
import './ChatInterface.css';
import ChatBody from './ChatBody';
import ChatComposer from './ChatComposer';
import ChatEmptyState from './ChatEmptyState';
import ChatHeader from './ChatHeader';
import ChatNoClientPreview from './ChatNoClientPreview';
import ModeSelector from './ModeSelector';
import { useLanguage } from '../../LanguageContext';
import VirtualKeyboard from './VirtualKeyboard';
import OarsInfoModal from './oars/OarsInfoModal';
import SpecificStrategyInfoModal from './strategies/SpecificStrategyInfoModal';
import useChatOarsMode from '../../hooks/useChatOarsMode';
import useChatSession from '../../hooks/useChatSession';
import useLlmMode from '../../hooks/useLlmMode';
import useVoiceMode from '../../hooks/useVoiceMode';
import useClientMode from '../../hooks/useClientMode';
import VoiceBetaModal from './voice/VoiceBetaModal';
import CumulativeTopicsPopover from './CumulativeTopicsPopover';
import { COUNSELLOR_MODES, DEFAULT_COUNSELLOR_MODE } from '../../counsellorModes';
const OVERLAY_OFFSET_JITTER_PX = 6;
const MIN_OVERLAY_BOTTOM_OFFSET_PX = 92;
const COMPOSER_CHAR_LIMIT = 1000;
const COMPOSER_LIMIT_FLASH_MS = 500;
const OARS_SKILL_TYPE_BY_CATEGORY = {
  OQ: 'open_question',
  AF: 'affirmation',
  SR: 'simple_reflection',
  CR: 'complex_reflection',
  S: 'summary',
};

const mapAppLanguageToKeyboardLayout = (language) => {
  if (language === 'ar') return 'ar';
  if (language === 'vi') return 'vi';
  return 'latin';
};

function ChatInterface({
  theme = 'dark',
  profileIdx,
  historicalReplay = null,
  customProfile,
  sessionActive,
  onSessionEnd,
  onSessionRegistered,
  onSessionReset,
  onConversationStart,
  onSystemMessagesUpdate,
  onTurnClassification,
  onTurnGuidance,
  onTurnGuidanceError,
  onRevealedItemsUpdate,
  onStageMetricsUpdate,
  showNotification,
  showConfirm,
  viewedTurnIndex,
  highlightedTurnIndex = null,
  totalTurns,
  fullSessionAnnotations = [],
  fullSessionVignette = null,
  revealedItems = { motivations: [], barriers: [], next_steps: [] },
  topicsPanelOpen = false,
  unreadTopicCount = 0,
  onTopicsPanelToggle,
  userFirstName,
  userAvatarPreference,
  sessionCounsellorMode = null,
  clientModeCounsellorMode = null,
  btcActivationRequest = 0,
  btcCueFlowActive = false,
  onBtcCueFlowActiveChange,
  activeMode = 'freeform',
  onFreeForm,
  onBeTheClient,
  onOars,
  onStrategies,
  strategiesFlow,
  oarsExercise,
  oarsExerciseLoading = false,
  oarsAttempt = null,
  isSpecificSkillReplay = false,
  oarsResetKey = 0,
  onAttempt,
  onOarsAnalyzingChange,
  onOarsStreamingEvaluationChange,
  oarsFilter = undefined,
  oarsExerciseIndex,
  oarsExerciseTotal,
  specificSkillsAttemptCount = 0,
  specificSkillsRetryAttemptId = null,
  disableFullSessionCues = false,
  guidedHighlightsEnabled = true,
  skillsAnnotationsEnabled = true,
  onOarsFilterChange,
  onOarsPopupBlockingChange,
  oarsInfoOpen: oarsInfoOpenProp,
  oarsInfoTab: oarsInfoTabProp,
  onOarsInfoOpen,
  onOarsInfoClose,
  oarsInfoShowCheckbox: oarsInfoShowCheckboxProp,
  dismissSkillPopups: dismissSkillPopupsProp,
  onDismissSkillPopupsChange,
  onLoadingChange,
  compactModeNavigator = false,
  compactSessionControls = false,
  hidePracticeModeNavigator = false,
  suppressAnnotationTooltips = false,
}) {
  const isHistoricalReplay = Boolean(historicalReplay);
  const isClientMode = !isHistoricalReplay && profileIdx === -2;
  // Which side of the transcript the user actually wrote. In an llm_mode replay
  // a virtual counsellor authored the counsellor turns, so neither side is
  // theirs and no message should carry their avatar.
  const humanSpeaker = isHistoricalReplay
    ? (
      historicalReplay.mode === 'llm_mode'
        ? null
        : historicalReplay.role === 'client' ? 'client' : 'counsellor'
    )
    : (isClientMode ? 'client' : 'counsellor');
  const isOarsMode = !isHistoricalReplay && profileIdx === -3;
  const isStrategiesMode = !isHistoricalReplay && profileIdx === -4;
  const [conversation, setConversation] = useState([]);
  const { t, language } = useLanguage();
  const [inputMessage, setInputMessage] = useState('');
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const [keyboardLayout, setKeyboardLayout] = useState(() => mapAppLanguageToKeyboardLayout(language));
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [endReason, setEndReason] = useState(null);
  const [counsellorMode, setCounsellorMode] = useState(DEFAULT_COUNSELLOR_MODE);
  const [showCounsellorDropdown, setShowCounsellorDropdown] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const composerRef = useRef(null);
  const turnAnchorRefs = useRef([]);
  const previousProfileRef = useRef(profileIdx);
  const previousSessionActiveRef = useRef(sessionActive);
  const dropdownRef = useRef(null);
  const [modeBarVisible, setModeBarVisible] = useState(true);
  const hasAutoCollapsedRef = useRef(false);
  const [oarsInfoOpenLocal, setOarsInfoOpenLocal] = useState(false);
  const [oarsInfoTabLocal, setOarsInfoTabLocal] = useState('SR');
  const oarsInfoOpen = oarsInfoOpenProp !== undefined ? oarsInfoOpenProp : oarsInfoOpenLocal;
  const oarsInfoTab = oarsInfoTabProp !== undefined ? oarsInfoTabProp : oarsInfoTabLocal;
  const setOarsInfoOpen = useCallback((val) => {
    if (oarsInfoOpenProp !== undefined) {
      if (val) {
        onOarsInfoOpen?.(oarsInfoTab);
      } else {
        onOarsInfoClose?.();
      }
      return;
    }
    setOarsInfoOpenLocal(val);
  }, [oarsInfoOpenProp, onOarsInfoClose, onOarsInfoOpen, oarsInfoTab]);
  const setOarsInfoTab = useCallback((tab) => {
    if (oarsInfoTabProp !== undefined) {
      onOarsInfoOpen?.(tab);
      return;
    }
    setOarsInfoTabLocal(tab);
  }, [oarsInfoTabProp, onOarsInfoOpen]);
  const [oarsInfoShowCheckboxLocal] = useState(false);
  const oarsInfoShowCheckbox = oarsInfoShowCheckboxProp !== undefined ? oarsInfoShowCheckboxProp : oarsInfoShowCheckboxLocal;
  const [dismissSkillPopupsLocal, setDismissSkillPopupsLocal] = useState(false);
  const dismissSkillPopups = dismissSkillPopupsProp !== undefined ? dismissSkillPopupsProp : dismissSkillPopupsLocal;
  const setDismissSkillPopups = (v) => dismissSkillPopupsProp !== undefined ? onDismissSkillPopupsChange?.(v) : setDismissSkillPopupsLocal(v);
  const [overlayBottomOffset, setOverlayBottomOffset] = useState(MIN_OVERLAY_BOTTOM_OFFSET_PX);
  const [showComposerLimitFlash, setShowComposerLimitFlash] = useState(false);
  const [selectedRandomSkillType, setSelectedRandomSkillType] = useState(null);
  const [randomSkillAttemptId, setRandomSkillAttemptId] = useState(null);
  const [randomSkillAttemptStarting, setRandomSkillAttemptStarting] = useState(false);
  const composerLimitFlashTimeoutRef = useRef(null);

  const oarsPopupBlocking = oarsInfoOpen && oarsInfoShowCheckbox;
  const triggerComposerLimitFlash = useCallback(() => {
    if (composerLimitFlashTimeoutRef.current) {
      window.clearTimeout(composerLimitFlashTimeoutRef.current);
    }
    setShowComposerLimitFlash(true);
    composerLimitFlashTimeoutRef.current = window.setTimeout(() => {
      setShowComposerLimitFlash(false);
      composerLimitFlashTimeoutRef.current = null;
    }, COMPOSER_LIMIT_FLASH_MS);
  }, []);

  useEffect(() => {
    onOarsPopupBlockingChange && onOarsPopupBlockingChange(oarsPopupBlocking);
  }, [oarsPopupBlocking, onOarsPopupBlockingChange]);

  useEffect(() => {
    return () => {
      if (composerLimitFlashTimeoutRef.current) {
        window.clearTimeout(composerLimitFlashTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    onLoadingChange && onLoadingChange(loading);
  }, [loading, onLoadingChange]);

  useEffect(() => {
    setKeyboardLayout(mapAppLanguageToKeyboardLayout(language));
  }, [language]);

  // Collapse the bar once when a session is registered or OARS mode is entered
  useEffect(() => {
    if (
      !isOarsMode &&
      !isClientMode &&
      profileIdx !== null &&
      profileIdx !== undefined &&
      !hasAutoCollapsedRef.current
    ) {
      hasAutoCollapsedRef.current = true;
      setModeBarVisible(false);
      return;
    }

    if (sessionId && !hasAutoCollapsedRef.current) {
      hasAutoCollapsedRef.current = true;
      setModeBarVisible(false);
    }
  }, [sessionId, profileIdx, isOarsMode, isClientMode]);

  useEffect(() => {
    if (isOarsMode && oarsExercise && !hasAutoCollapsedRef.current) {
      hasAutoCollapsedRef.current = true;
      setModeBarVisible(false);
    }
  }, [isOarsMode, oarsExercise]);

  // When a BTC conversation ends, bring the mode bar back so the user can switch modes
  useEffect(() => {
    if (isClientMode && sessionEnded) {
      setModeBarVisible(true);
    }
  }, [isClientMode, sessionEnded]);

  useEffect(() => {
    if (sessionEnded) {
      setKeyboardOpen(false);
    }
  }, [sessionEnded]);

  useEffect(() => {
    const composer = composerRef.current;
    if (!composer) return;

    const updateOffset = () => {
      const nextOffset = Math.max(composer.offsetHeight + 8, MIN_OVERLAY_BOTTOM_OFFSET_PX);
      setOverlayBottomOffset((currentOffset) => {
        if (Math.abs(nextOffset - currentOffset) <= OVERLAY_OFFSET_JITTER_PX) {
          return currentOffset;
        }
        return nextOffset;
      });
    };

    updateOffset();

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(updateOffset)
      : null;

    resizeObserver?.observe(composer);
    window.addEventListener('resize', updateOffset);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateOffset);
    };
  }, [inputMessage, modeBarVisible, isOarsMode, isClientMode, isHistoricalReplay, profileIdx, sessionEnded]);

  const resizeComposerTextarea = useCallback(() => {
    const textarea = inputRef.current;
    if (!textarea || textarea.tagName !== 'TEXTAREA') return;

    textarea.style.height = 'auto';

    const computedStyle = window.getComputedStyle(textarea);
    const lineHeight = parseFloat(computedStyle.lineHeight) || 24;
    const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
    const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;
    const borderTop = parseFloat(computedStyle.borderTopWidth) || 0;
    const borderBottom = parseFloat(computedStyle.borderBottomWidth) || 0;
    const maxHeight = (lineHeight * 3) + paddingTop + paddingBottom + borderTop + borderBottom;
    const nextHeight = Math.min(textarea.scrollHeight, maxHeight);

    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, []);

  useEffect(() => {
    resizeComposerTextarea();
  }, [resizeComposerTextarea, inputMessage, isOarsMode, isClientMode, sessionEnded]);

  useEffect(() => {
    const textarea = inputRef.current;
    const inputShell = textarea?.parentElement;
    const composer = composerRef.current;
    if (!textarea || !inputShell) return;

    let lastObservedWidth = inputShell.getBoundingClientRect().width;
    let resizeFrame = null;

    const resizeOnNextFrame = () => {
      if (resizeFrame !== null) {
        window.cancelAnimationFrame(resizeFrame);
      }
      resizeFrame = window.requestAnimationFrame(() => {
        resizeFrame = null;
        resizeComposerTextarea();
      });
    };

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(([entry]) => {
        const nextWidth = inputShell.getBoundingClientRect().width || entry?.contentRect?.width || 0;
        if (Math.abs(nextWidth - lastObservedWidth) < 1) return;
        lastObservedWidth = nextWidth;
        resizeOnNextFrame();
      })
      : null;

    resizeObserver?.observe(inputShell);
    if (composer && composer !== inputShell) {
      resizeObserver?.observe(composer);
    }
    window.addEventListener('resize', resizeOnNextFrame);

    return () => {
      if (resizeFrame !== null) {
        window.cancelAnimationFrame(resizeFrame);
      }
      resizeObserver?.disconnect();
      window.removeEventListener('resize', resizeOnNextFrame);
    };
  }, [resizeComposerTextarea, profileIdx, sessionId, isOarsMode, isClientMode, isHistoricalReplay]);

  const notify = useMemo(
    () => showNotification || ((message) => window.alert(message)),
    [showNotification]
  );
  const confirmDialog = useMemo(
    () => (showConfirm ? showConfirm : (message) => Promise.resolve(window.confirm(message))),
    [showConfirm]
  );
  const {
    resolveClientType,
    initSession,
    handleFreeformSend,
  } = useChatSession({
    profileIdx,
    customProfile,
    isOarsMode,
    isStrategiesMode,
    isClientMode,
    sessionActive,
    sessionId,
    conversationLength: conversation.length,
    onConversationStart,
    onSessionRegistered,
    onSystemMessagesUpdate,
    onTurnClassification,
    onTurnGuidance,
    onTurnGuidanceError,
    onRevealedItemsUpdate,
    onStageMetricsUpdate,
    onSessionEnd,
    notify,
    setLoading,
    setConversation,
    setSessionId,
    setSessionEnded,
    setEndReason,
  });
  // Scroll the message list itself rather than scrollIntoView on the end
  // marker: scrollIntoView walks up and scrolls every scrollable ancestor,
  // including the document, which drags the whole page on mobile.
  const scrollToBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = 0;
      }
    });
    return () => window.cancelAnimationFrame(frameId);
  }, []);

  useEffect(() => {
    if (!isStrategiesMode || !strategiesFlow?.exercise) return;
    const frameId = window.requestAnimationFrame(scrollToBottom);
    return () => window.cancelAnimationFrame(frameId);
  }, [
    isStrategiesMode,
    scrollToBottom,
    strategiesFlow?.conversation?.length,
    strategiesFlow?.exercise,
    strategiesFlow?.loading,
  ]);

  const {
    llmMode,
    llmPaused,
    llmFinishingUtterance,
    setLlmMode,
    abortLlmStream,
    handleLlmMode,
    handlePauseLlmMode,
  } = useLlmMode({
    profileIdx,
    conversationLength: conversation.length,
    counsellorModes: COUNSELLOR_MODES.map((mode) => ({
      ...mode,
      label: t(mode.labelKey),
    })),
    counsellorMode,
    confirmDialog,
    resolveClientType,
    onConversationStart,
    onSessionRegistered,
    onSystemMessagesUpdate,
    onTurnClassification,
    onRevealedItemsUpdate,
    onStageMetricsUpdate,
    onSessionEnd,
    notify,
    setLoading,
    setConversation,
    setSessionId,
    setSessionEnded,
    setEndReason,
    scrollToBottom,
  });

  const resetChatSessionState = useCallback((options = {}) => {
    const { keepLoading = false } = options;
    setConversation([]);
    setSessionId(null);
    setSessionEnded(false);
    setEndReason(null);
    setInputMessage('');
    if (!keepLoading) {
      setLoading(false);
    }
    setLlmMode(false);
  }, [setLlmMode]);

  useEffect(() => {
    if (!historicalReplay) {
      return;
    }

    setConversation(historicalReplay.conversation || []);
    setSessionEnded(true);
    setEndReason(historicalReplay.analysis_status?.end_reason || null);
    setInputMessage('');
    setLoading(false);
    setLlmMode(false);
    if (historicalReplay.mode === 'llm_mode' && historicalReplay.counsellor_mode) {
      setCounsellorMode(historicalReplay.counsellor_mode);
    }
  }, [historicalReplay, setLlmMode]);

  // Detect session restart (sessionActive goes from false -> true with same profile)
  useEffect(() => {
    if (historicalReplay) {
      return;
    }
    if (
      !isStrategiesMode &&
      sessionActive &&
      !previousSessionActiveRef.current &&
      profileIdx === previousProfileRef.current &&
      profileIdx !== null
    ) {
      // Disconnect LLM stream if running
      abortLlmStream();

      // Cancel backend LLM mode if running
      if (llmMode) {
        cancelLlmMode().catch(err => console.error('Error cancelling LLM mode:', err));
      }

      // Session restarted with same client - clear all state
      console.log('Session restarted - clearing conversation');
      resetChatSessionState();
      // Initialize a new session
      initSession();
    }
    previousSessionActiveRef.current = sessionActive;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historicalReplay, sessionActive, profileIdx, isStrategiesMode]);

  // Initialize session when profile is selected
  useEffect(() => {
    if (historicalReplay) {
      return;
    }
    if (!isStrategiesMode && profileIdx !== null && !sessionId) {
      initSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historicalReplay, profileIdx, isStrategiesMode]);

  // Reset state when profile changes (switching clients, not on initial mount)
  useEffect(() => {
    if (historicalReplay) {
      previousProfileRef.current = profileIdx;
      return;
    }
    if (
      profileIdx !== null &&
      previousProfileRef.current !== null &&
      previousProfileRef.current !== profileIdx
    ) {
      disconnectRealtime();

      // Disconnect LLM stream if running
      abortLlmStream();

      // Cancel backend LLM mode if running
      if (llmMode) {
        cancelLlmMode().catch(err => console.error('Error cancelling LLM mode:', err));
      }

      // User is switching to a different client - clear all state
      resetChatSessionState();
      resetVoiceModeUi();
      // Initialize new sessions only for modes backed by a conversation session.
      if (!isStrategiesMode) {
        initSession();
      }
    }
    previousProfileRef.current = profileIdx;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historicalReplay, profileIdx, isStrategiesMode]);

  // Reset counsellor mode to default when entering client mode
  useEffect(() => {
    if (profileIdx === -2) {
      setCounsellorMode(clientModeCounsellorMode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileIdx, clientModeCounsellorMode]);

  useEffect(() => {
    if (!isClientMode && !isOarsMode && !counsellorMode) {
      setCounsellorMode(DEFAULT_COUNSELLOR_MODE);
    }
  }, [isClientMode, isOarsMode, counsellorMode]);

  // Close counsellor dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowCounsellorDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleRestartSession = async () => {
    const confirmed = await confirmDialog(
      "Restart session?\n\n" +
      "This will clear the current conversation and start a new session with the same client.\n\n" +
      "Continue?"
    );

    if (!confirmed) return;

    // Clear insights/session state in App
    if (onSessionReset) onSessionReset();

    try {
      setLoading(true);

      // Disconnect LLM stream if running
      abortLlmStream();

      // Cancel backend LLM mode if running
      if (llmMode) {
        try {
          await cancelLlmMode();
          console.log('LLM mode cancelled on backend');
        } catch (err) {
          console.error('Error cancelling LLM mode:', err);
        }
      }

      // In YAC mode, wipe the backend session so the old conversation can't receive stale messages
      if (isClientMode) {
        await resetSession().catch(err => console.error('Error resetting session:', err));
      }

      resetChatSessionState({ keepLoading: !isClientMode });

      // In YAC mode, wait for user to re-activate the counsellor
      if (!isClientMode) {
        await initSession();
      } else {
        setLoading(false);
      }
    } catch (err) {
      console.error('Failed to restart session:', err);
      notify('Failed to restart session. Please try again.');
      setLoading(false);
    }
  };

  const handleForceEndSession = async () => {
    if (
      conversation.length === 0 ||
      loading ||
      sessionEnded ||
      !sessionActive ||
      !sessionId
    ) {
      return;
    }

    const confirmed = await confirmDialog(
      "End session now?\n\n" +
      "This will stop the conversation immediately and proceed to session analysis.\n\n" +
      "Continue?"
    );

    if (!confirmed) return;

    try {
      setLoading(true);
      if (llmMode) {
        try {
          await cancelLlmMode();
        } catch (err) {
          console.error('Error cancelling LLM mode before ending session:', err);
        }
        setLlmMode(false);
      }
      await endSession();
      setSessionEnded(true);
      setEndReason('Session ended manually');

      if (onSessionEnd) {
        onSessionEnd(sessionId);
      }
    } catch (err) {
      console.error('Failed to end session:', err);
      notify('Failed to end session. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Auto-scroll to bottom when new messages arrive (but not when paused)
  useEffect(() => {
    if (conversation.length > 0 && !llmPaused) {
      scrollToBottom();
    }
  }, [conversation, llmPaused, scrollToBottom]);

  // Auto-focus is a desktop convenience. On touch-sized single-column views,
  // focusing here opens the OS keyboard immediately after client selection and
  // causes iOS WKWebView to pan the entire visual viewport.
  useEffect(() => {
    if (!hidePracticeModeNavigator && !loading && !sessionEnded && sessionActive && !llmMode) {
      inputRef.current?.focus();
    }
  }, [hidePracticeModeNavigator, loading, sessionEnded, sessionActive, llmMode]);

  // Scroll selected turn into view when navigating history
  useEffect(() => {
    if (viewedTurnIndex === null) {
      if (conversation.length > 0) {
        scrollToBottom();
      }
      return;
    }
    const container = messagesContainerRef.current;
    const target = turnAnchorRefs.current[viewedTurnIndex];
    if (container && target) {
      const containerTop = container.getBoundingClientRect().top;
      const targetTop = target.getBoundingClientRect().top;
      const offset = targetTop - containerTop;
      container.scrollTo({
        top: container.scrollTop + offset - 12,
        behavior: 'smooth'
      });
    }
  }, [viewedTurnIndex, conversation.length, scrollToBottom]);

  const {
    oarsResult,
    oarsSubmitting,
    oarsPendingUtterance,
    handleOarsSubmit,
  } = useChatOarsMode({
    isOarsMode,
    oarsExercise,
    oarsResetKey,
    oarsPopupBlocking,
    inputMessage,
    setInputMessage,
    inputRef,
    messagesContainerRef,
    onAttempt,
    onOarsAnalyzingChange,
    onOarsStreamingEvaluationChange,
    triggerComposerLimitFlash,
    composerCharLimit: COMPOSER_CHAR_LIMIT,
    specificSkillAttemptId: randomSkillAttemptId,
    retryOfAttemptId: specificSkillsRetryAttemptId,
    selectedSkillType: selectedRandomSkillType,
    autoFocusInput: !hidePracticeModeNavigator,
  });
  const isRandomSkillMode = isOarsMode && !oarsFilter;
  const showRandomSkillChoicePanel =
    !isSpecificSkillReplay &&
    isRandomSkillMode &&
    !!oarsExercise &&
    !oarsExerciseLoading &&
    !oarsResult;
  const isRandomSkillChoicePending = showRandomSkillChoicePanel && !selectedRandomSkillType;
  const showSpecificSkillsFirstCue =
    !isSpecificSkillReplay &&
    guidedHighlightsEnabled &&
    isOarsMode &&
    !!oarsExercise &&
    !oarsInfoOpen &&
    !oarsResult &&
    !oarsSubmitting &&
    !isRandomSkillChoicePending;
  const {
    isConsentPending,
    isBtcFirstUserTurnReady,
    handleClientModeSend,
    handleConsentChoice,
  } = useClientMode({
    isClientMode,
    sessionId,
    sessionEnded,
    loading,
    conversationLength: conversation.length,
    counsellorMode,
    clientModeCounsellorMode,
    btcActivationRequest,
    btcCueFlowActive,
    onBtcCueFlowActiveChange,
    onConversationStart,
    onSessionRegistered: (nextSessionId) => {
      setSessionId(nextSessionId);
      onSessionRegistered?.(nextSessionId);
    },
    onSystemMessagesUpdate,
    onStageMetricsUpdate,
    onSessionEnd,
    notify,
    setLoading,
    setConversation,
    setSessionEnded,
    setEndReason,
  });
  const showBtcConsentCue = guidedHighlightsEnabled && btcCueFlowActive && isConsentPending;
  const showBtcComposerCue = guidedHighlightsEnabled && isClientMode && isBtcFirstUserTurnReady;
  const {
    voiceMode,
    voiceStatus,
    voiceBetaModalOpen,
    handleEnterVoiceMode,
    handleBeginVoiceMode,
    closeVoiceBetaModal,
    resetVoiceModeUi,
    disconnectRealtime,
  } = useVoiceMode({
    enabled: !isOarsMode && !isStrategiesMode,
    role: isClientMode ? 'client' : 'counsellor',
    sessionActive,
    sessionEnded,
    sessionId,
    conversationLength: conversation.length,
    onConversationStart,
    onConversationAppend: (message) => {
      if (isClientMode && btcCueFlowActive) {
        onBtcCueFlowActiveChange?.(false);
      }
      // A late transcript must land before the contemplating placeholder,
      // never after it.
      setConversation((prev) => {
        const idx = prev.findIndex((m) => m.isStreaming);
        if (idx === -1) return [...prev, message];
        return [...prev.slice(0, idx), message, ...prev.slice(idx)];
      });
    },
    onClassification: onTurnClassification,
    onGuidance: onTurnGuidance,
    onGuidanceError: onTurnGuidanceError,
    onAnalytics: (data) => {
      if (onSystemMessagesUpdate) {
        onSystemMessagesUpdate(
          data.system_messages?.counsellor || [],
          data.system_messages?.client || [],
          data.stage_metrics || null,
          data.working_alliance_series || [],
          data.all_tasks_series || [],
          {
            automitiLabels: data.automiti_labels || [],
            evaluation: data.evaluation || null,
            traineeText: data.counsellor_message || '',
          }
        );
      }
      if (onRevealedItemsUpdate && data.revealed_items) {
        onRevealedItemsUpdate(data.revealed_items);
      }
      if (onStageMetricsUpdate && data.stage_metrics) {
        onStageMetricsUpdate(data.stage_metrics);
      }
    },
    onSessionEnded: (reason) => {
      setSessionEnded(true);
      setEndReason(reason);
      if (onSessionEnd) {
        onSessionEnd(sessionId);
      }
    },
    setConversation,
    setLoading,
    notify,
  });

  const handleSendMessage = useCallback(async (e) => {
    e?.preventDefault();
    if (!inputMessage.trim() || loading || !sessionActive) return;
    if (isClientMode && !sessionId) return; // must activate counsellor first
    if (inputMessage.length > COMPOSER_CHAR_LIMIT) {
      triggerComposerLimitFlash();
      return;
    }

    const messageToSend = inputMessage;
    setInputMessage('');
    setLoading(true);

    if (isClientMode) {
      await handleClientModeSend(messageToSend);
      return;
    }

    await handleFreeformSend(messageToSend);
  }, [
    inputMessage,
    loading,
    sessionActive,
    isClientMode,
    sessionId,
    handleClientModeSend,
    handleFreeformSend,
    triggerComposerLimitFlash,
  ]);

  const handleStrategiesSubmit = useCallback(async (e) => {
    e?.preventDefault();
    const messageToSend = inputMessage.trim();
    if (
      !messageToSend ||
      messageToSend.length > COMPOSER_CHAR_LIMIT ||
      strategiesFlow?.loading ||
      strategiesFlow?.status !== 'active'
    ) {
      if (inputMessage.length > COMPOSER_CHAR_LIMIT) triggerComposerLimitFlash();
      return;
    }

    setInputMessage('');
    const result = await strategiesFlow.submitResponse?.(messageToSend);
    if (!result) setInputMessage(messageToSend);
  }, [inputMessage, strategiesFlow, triggerComposerLimitFlash]);

  const handleInputKeyDown = useCallback((e) => {
    if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) {
      return;
    }

    e.preventDefault();
    if (isOarsMode) {
      handleOarsSubmit();
      return;
    }
    if (isStrategiesMode) {
      handleStrategiesSubmit();
      return;
    }
    handleSendMessage();
  }, [handleSendMessage, handleOarsSubmit, handleStrategiesSubmit, isOarsMode, isStrategiesMode]);

  useEffect(() => {
    if (!showRandomSkillChoicePanel) {
      setSelectedRandomSkillType(null);
      setRandomSkillAttemptId(null);
      setRandomSkillAttemptStarting(false);
      return;
    }
    setSelectedRandomSkillType(null);
    setRandomSkillAttemptId(null);
    setRandomSkillAttemptStarting(false);
  }, [showRandomSkillChoicePanel, oarsExercise?.id]);

  const handleSelectRandomSkillType = useCallback(async (value) => {
    if (randomSkillAttemptStarting) {
      return;
    }
    setSelectedRandomSkillType(value);
    if (!showRandomSkillChoicePanel || !oarsExercise) {
      return;
    }
    setRandomSkillAttemptStarting(true);
    try {
      const data = await startOarsAttempt({
        exerciseId: oarsExercise.id,
        skillType: OARS_SKILL_TYPE_BY_CATEGORY[oarsExercise.oars_category],
        generatedExerciseId: oarsExercise.generated_exercise_id || null,
        selectedSkillType: value,
        retryOfAttemptId: specificSkillsRetryAttemptId,
      });
      setRandomSkillAttemptId(data.attempt_id || null);
    } catch (err) {
      console.error('Failed to start random-mode OARS attempt:', err);
      setSelectedRandomSkillType(null);
    } finally {
      setRandomSkillAttemptStarting(false);
    }
  }, [oarsExercise, randomSkillAttemptStarting, showRandomSkillChoicePanel, specificSkillsRetryAttemptId]);

  const showWelcomePanel = !isHistoricalReplay && !isOarsMode && !isStrategiesMode && conversation.length === 0 && !llmMode && (isClientMode || (profileIdx !== null && profileIdx !== undefined));
  const showInlineModeSelector =
    !isHistoricalReplay &&
    (
      (isClientMode && showWelcomePanel) ||
      (isOarsMode && !oarsExercise && !oarsExerciseLoading) ||
      (isStrategiesMode && !strategiesFlow?.exercise && !strategiesFlow?.loading)
    );
  const isClientModeInactive = isClientMode && !sessionId;
  const isTextInputBlocked = isSpecificSkillReplay || voiceMode || !sessionActive || loading || llmMode || sessionEnded || isClientModeInactive || isConsentPending;
  const showFullSessionComposerCue =
    !disableFullSessionCues &&
    !isOarsMode &&
    !isStrategiesMode &&
    !isClientMode &&
    profileIdx !== null &&
    profileIdx !== undefined &&
    !isTextInputBlocked;
  const showStrategiesComposerCue =
    guidedHighlightsEnabled &&
    isStrategiesMode &&
    !!strategiesFlow?.exercise &&
    !strategiesFlow?.loading &&
    strategiesFlow?.status === 'active';
  const showComposerSelectionCue =
    showFullSessionComposerCue ||
    showSpecificSkillsFirstCue ||
    showStrategiesComposerCue ||
    showBtcComposerCue;
  const isComposerPreviewDisabled =
    isClientModeInactive ||
    (isOarsMode && (!oarsExercise || oarsExerciseLoading)) ||
    (isStrategiesMode && !strategiesFlow?.exercise);
  const isOarsInputDisabled = isSpecificSkillReplay || oarsSubmitting || !!oarsResult || !oarsExercise || oarsExerciseLoading || isRandomSkillChoicePending || randomSkillAttemptStarting;
  const isOarsSubmitDisabled = isSpecificSkillReplay || !oarsExercise || !inputMessage.trim() || oarsSubmitting || !!oarsResult || oarsExerciseLoading || isRandomSkillChoicePending || randomSkillAttemptStarting;
  const isStrategiesInputDisabled =
    !strategiesFlow?.exercise ||
    strategiesFlow?.loading ||
    strategiesFlow?.status !== 'active';
  const strategiesInputPlaceholder = !strategiesFlow?.exercise
    ? 'Select a strategy to begin.'
    : strategiesFlow?.loading || strategiesFlow?.status === 'evaluating'
      ? t('chat_placeholder_evaluating')
      : strategiesFlow?.status === 'complete'
        ? t('chat_placeholder_oars_done')
        : 'What would you say next?';
  const isSendDisabled = isStrategiesMode
    ? isStrategiesInputDisabled || !inputMessage.trim()
    : isOarsMode
      ? isOarsSubmitDisabled
      : (isTextInputBlocked || !inputMessage.trim());
  const isVoiceModeDisabled = isOarsMode || isStrategiesMode || (isClientMode && isConsentPending) || !sessionActive || sessionEnded || !sessionId || (voiceMode && voiceStatus === 'connecting');
  const isEndSessionDisabled = isOarsMode || isStrategiesMode || conversation.length === 0 || loading || sessionEnded || !sessionActive || !sessionId;
  const isRestartSessionDisabled = isOarsMode || isStrategiesMode || conversation.length === 0 || loading;
  const isLlmControlDisabled = loading || llmMode || sessionEnded;
  const suppressModeNavigator = compactModeNavigator || hidePracticeModeNavigator;
  const singleColumnEmptyPrompt = hidePracticeModeNavigator
    ? isOarsMode
      ? (!oarsExercise && !oarsExerciseLoading ? 'Choose a skill to begin.' : '')
      : isStrategiesMode
        ? (!strategiesFlow?.exercise && !strategiesFlow?.loading ? 'Choose a strategy to begin.' : '')
      : isClientMode
        ? (!clientModeCounsellorMode && !sessionId ? 'Choose a counsellor to begin.' : '')
        : (profileIdx === null || profileIdx === undefined ? 'Choose a client to begin.' : '')
    : '';
  const renderCompactModeNavigator = (belowHeader = false) => (
    compactModeNavigator && !hidePracticeModeNavigator ? (
      <div className={`chat-compact-mode-selector-shell${belowHeader ? ' chat-compact-mode-selector-shell--below-header' : ''}`}>
        <ModeSelector
          modeBarVisible
          setModeBarVisible={() => {}}
          activeMode={activeMode}
          onFreeForm={onFreeForm}
          onBeTheClient={onBeTheClient}
          onOars={onOars}
          onStrategies={onStrategies}
          variant="compact"
        />
      </div>
    ) : null
  );

  const handleKeyboardInsert = useCallback((value) => {
    if (isStrategiesMode ? isStrategiesInputDisabled : isTextInputBlocked) {
      return;
    }
    setInputMessage((prev) => `${prev}${value}`);
    inputRef.current?.focus();
  }, [isStrategiesInputDisabled, isStrategiesMode, isTextInputBlocked]);

  const handleKeyboardBackspace = useCallback(() => {
    if (isStrategiesMode ? isStrategiesInputDisabled : isTextInputBlocked) {
      return;
    }
    setInputMessage((prev) => prev.slice(0, -1));
    inputRef.current?.focus();
  }, [isStrategiesInputDisabled, isStrategiesMode, isTextInputBlocked]);

  const handleKeyboardSpace = useCallback(() => {
    if (isStrategiesMode ? isStrategiesInputDisabled : isTextInputBlocked) {
      return;
    }
    setInputMessage((prev) => `${prev} `);
    inputRef.current?.focus();
  }, [isStrategiesInputDisabled, isStrategiesMode, isTextInputBlocked]);
  // No profile selected state (skip for client mode which uses sentinel -2)
  if (!isHistoricalReplay && !isClientMode && (profileIdx === null || profileIdx === undefined)) {
    return (
      <div className="chat-interface">
        {renderCompactModeNavigator(false)}
        <ChatEmptyState
          t={t}
          theme={theme}
          variant="no-client"
          showModeSelector={!suppressModeNavigator}
          hideTitle={suppressModeNavigator}
          hideDescription={suppressModeNavigator}
          singleColumnEmptyPrompt={singleColumnEmptyPrompt}
          activeMode={activeMode}
          onFreeForm={onFreeForm}
          onBeTheClient={onBeTheClient}
          onOars={onOars}
          onStrategies={onStrategies}
        />
        <ChatNoClientPreview
          composerRef={composerRef}
          compactSessionControls={compactSessionControls}
        />
      </div>
    );
  }

  const clientHeading = profileIdx === -1 ? t('chat_client_custom') : (profileIdx !== null && profileIdx !== -2 ? t('chat_client_virtual', { id: profileIdx }) : t('chat_client_virtual_default'));
  const counsellorLabelKey = COUNSELLOR_MODES.find(m => m.value === counsellorMode)?.labelKey ?? null;
  const counsellorLabel = counsellorLabelKey ? t(counsellorLabelKey) : '';
  const headingModeLabel = t('chat_mode_profile_reveal');
  const showChatHeader =
    (!isOarsMode && !isStrategiesMode && !isClientMode) ||
    (isClientMode && (sessionId || sessionEnded)) ||
    (isOarsMode && (!!oarsExercise || oarsExerciseLoading)) ||
    (isStrategiesMode && (!!strategiesFlow?.exercise || strategiesFlow?.loading));
  const showCumulativeTopics = !isOarsMode && !isStrategiesMode && !isClientMode && showChatHeader;
  const strategyTurnCount = (strategiesFlow?.conversation || []).filter(
    (turn, index) => (
      index >= (strategiesFlow?.exercise?.turn_count || 0)
      && turn.speaker === 'counsellor'
    ),
  ).length;
  turnAnchorRefs.current = [];
  return (
    <div className="chat-interface">
      {showChatHeader && (
        <ChatHeader
          t={t}
          isOarsMode={isOarsMode}
          isStrategiesMode={isStrategiesMode}
          strategyTask={strategiesFlow?.selectedTask}
          strategyName={strategiesFlow?.selectedStrategy}
          strategyTurnCount={strategyTurnCount}
          isClientMode={isClientMode}
          llmMode={llmMode}
          oarsFilter={oarsFilter}
          oarsExercise={oarsExercise}
          oarsExerciseIndex={oarsExerciseIndex}
          oarsExerciseTotal={oarsExerciseTotal}
          counsellorLabel={counsellorLabel}
          clientHeading={clientHeading}
          profileIdx={profileIdx}
          headingModeLabel={headingModeLabel}
          useCompactHeader={hidePracticeModeNavigator}
          dropdownRef={dropdownRef}
          handleLlmMode={handleLlmMode}
          isLlmControlDisabled={isLlmControlDisabled}
          sessionEnded={sessionEnded}
          showCounsellorDropdown={showCounsellorDropdown}
          setShowCounsellorDropdown={setShowCounsellorDropdown}
          counsellorMode={counsellorMode}
          sessionCounsellorMode={sessionCounsellorMode}
          counsellorModes={COUNSELLOR_MODES}
          setCounsellorMode={setCounsellorMode}
        />
      )}

      {renderCompactModeNavigator(showChatHeader)}

      <ChatBody
        t={t}
        theme={theme}
        isOarsMode={isOarsMode}
        isStrategiesMode={isStrategiesMode}
        isClientMode={isClientMode}
        humanSpeaker={humanSpeaker}
        showWelcomePanel={showWelcomePanel}
        showInlineModeSelector={!suppressModeNavigator && showInlineModeSelector}
        hideEmptyStateTitle={suppressModeNavigator}
        hideEmptyStateDescription={suppressModeNavigator}
        singleColumnEmptyPrompt={singleColumnEmptyPrompt}
        activeMode={activeMode}
        onFreeForm={onFreeForm}
        onBeTheClient={onBeTheClient}
        onOars={onOars}
        onStrategies={onStrategies}
        oarsExercise={oarsExercise}
        oarsExerciseLoading={oarsExerciseLoading}
        strategiesFlow={strategiesFlow}
        oarsAttempt={oarsAttempt}
        isSpecificSkillReplay={isSpecificSkillReplay}
        oarsFilter={oarsFilter}
        oarsPopupBlocking={oarsPopupBlocking}
        oarsPendingUtterance={oarsPendingUtterance}
        showRandomSkillChoicePanel={showRandomSkillChoicePanel}
        selectedRandomSkillType={selectedRandomSkillType}
        onSelectRandomSkillType={handleSelectRandomSkillType}
        showRandomSkillChoiceCue={guidedHighlightsEnabled && isRandomSkillChoicePending}
        skillsAnnotationsEnabled={skillsAnnotationsEnabled}
        suppressAnnotationTooltips={suppressAnnotationTooltips}
        conversation={conversation}
        viewedTurnIndex={viewedTurnIndex}
        highlightedTurnIndex={highlightedTurnIndex}
        totalTurns={totalTurns}
        fullSessionAnnotations={fullSessionAnnotations}
        llmMode={llmMode}
        llmPaused={llmPaused}
        llmFinishingUtterance={llmFinishingUtterance}
        sessionEnded={sessionEnded}
        isConsentPending={isConsentPending}
        showBtcConsentCue={showBtcConsentCue}
        handleConsentChoice={handleConsentChoice}
        userAvatarPreference={userAvatarPreference}
        messagesContainerRef={messagesContainerRef}
        messagesEndRef={messagesEndRef}
        turnAnchorRefs={turnAnchorRefs}
      />

      {showCumulativeTopics && !hidePracticeModeNavigator && (
        <CumulativeTopicsPopover
          t={t}
          clientDescription={fullSessionVignette?.patient_description}
          revealedItems={revealedItems}
          open={topicsPanelOpen}
          unreadCount={unreadTopicCount}
          onToggle={onTopicsPanelToggle}
          placement="desktop"
          style={{ bottom: `${overlayBottomOffset}px` }}
        />
      )}

      <ChatComposer
        composerRef={composerRef}
        isComposerPreviewDisabled={isComposerPreviewDisabled}
        isOarsMode={isOarsMode}
        isStrategiesMode={isStrategiesMode}
        handleOarsSubmit={handleOarsSubmit}
        handleStrategiesSubmit={handleStrategiesSubmit}
        handleSendMessage={handleSendMessage}
        handleForceEndSession={handleForceEndSession}
        isEndSessionDisabled={isEndSessionDisabled}
        handleRestartSession={handleRestartSession}
        isRestartSessionDisabled={isRestartSessionDisabled}
        voiceMode={voiceMode}
        voiceStatus={voiceStatus}
        handleEnterVoiceMode={handleEnterVoiceMode}
        isClientMode={isClientMode}
        isVoiceModeDisabled={isVoiceModeDisabled}
        showComposerSelectionCue={showComposerSelectionCue}
        showComposerLimitFlash={showComposerLimitFlash}
        inputRef={inputRef}
        inputMessage={inputMessage}
        setInputMessage={setInputMessage}
        handleInputKeyDown={handleInputKeyDown}
        oarsExercise={oarsExercise}
        oarsResult={oarsResult}
        oarsSubmitting={oarsSubmitting}
        llmMode={llmMode}
        counsellorLabel={counsellorLabel}
        sessionEnded={sessionEnded}
        endReason={endReason}
        isConsentPending={isConsentPending}
        sessionId={sessionId}
        t={t}
        isOarsInputDisabled={isOarsInputDisabled}
        isStrategiesInputDisabled={isStrategiesInputDisabled}
        strategiesInputPlaceholder={strategiesInputPlaceholder}
        isTextInputBlocked={isTextInputBlocked}
        setKeyboardOpen={setKeyboardOpen}
        isSendDisabled={isSendDisabled}
        overlayBottomOffset={overlayBottomOffset}
        llmPaused={llmPaused}
        llmFinishingUtterance={llmFinishingUtterance}
        handlePauseLlmMode={handlePauseLlmMode}
        showBtcConsentCue={showBtcConsentCue}
        handleConsentChoice={handleConsentChoice}
        modeBarVisible={modeBarVisible}
        setModeBarVisible={setModeBarVisible}
        activeMode={activeMode}
        onFreeForm={onFreeForm}
        onBeTheClient={onBeTheClient}
        onOars={onOars}
        onStrategies={onStrategies}
        hideModeSelector={showInlineModeSelector || suppressModeNavigator}
        compactSessionControls={compactSessionControls}
      />
      {keyboardOpen && (
        <VirtualKeyboard
          layout={keyboardLayout}
          onInsert={handleKeyboardInsert}
          onBackspace={handleKeyboardBackspace}
          onSpace={handleKeyboardSpace}
          onClose={() => setKeyboardOpen(false)}
        />
      )}

      <OarsInfoModal
        oarsInfoOpen={oarsInfoOpen}
        setOarsInfoOpen={setOarsInfoOpen}
        oarsInfoTab={oarsInfoTab}
        setOarsInfoTab={setOarsInfoTab}
        oarsInfoShowCheckbox={oarsInfoShowCheckbox}
        dismissSkillPopups={dismissSkillPopups}
        setDismissSkillPopups={setDismissSkillPopups}
      />

      <SpecificStrategyInfoModal
        open={Boolean(isStrategiesMode && strategiesFlow?.strategyInfoOpen)}
        onClose={strategiesFlow?.closeStrategyInfo}
        showCheckbox={Boolean(strategiesFlow?.strategyInfoShowCheckbox)}
        dismissStrategyPopups={Boolean(strategiesFlow?.dismissStrategyPopups)}
        onDismissStrategyPopupsChange={strategiesFlow?.setDismissStrategyPopups}
      />

      <VoiceBetaModal
        open={voiceBetaModalOpen}
        onClose={closeVoiceBetaModal}
        onBegin={handleBeginVoiceMode}
        role={isClientMode ? 'client' : 'counsellor'}
      />

    </div>
  );
}

export default ChatInterface;
